// The schedule's figures as legacy's back office prints them (SCR-077, the week grid and the Day view; legacy
// ScheduleCalendar.vue, ScheduleDayView.vue, useShiftCosts.ts, library/schedule.ts), computed the way legacy computes
// them: in JavaScript doubles, from each shift's paid hours as the API sends them (`net_hours`, PHP
// `round(minutes / 60, 2)`).
//   - Hours: the shifts' hours summed, printed to one decimal ("7.3 hrs"); a Day view hour column to two.
//   - Planned cost: each shift's hours times its rate (the role's when the role sets one, else the employee's), summed,
//     printed as `(cents / 100).toFixed(2)`; null (a dash) when no shift has a rate. A Day view column charges the
//     minutes each shift spends in it and rounds to the cent first.
//   - The Day view's business day runs 4am to 4am (legacy `BUSINESS_DAY_START_HOUR`), so an overnight shift is one bar;
//     the part of one past 4am is drawn on the next day as a continuation, counted there in the hour columns only.
// FIX B339 (after parity: money, L495): rounding each shift to hundredths of an hour before multiplying loses up to
// 0.3 minutes a shift (20 minutes costs 0.33 hours: three at $10.01 are $9.91 where they cost $10.01). The exact
// arithmetic, minutes times rate rounded once, is built behind `SCHEDULE_ARITHMETIC` and stays off until parity is
// validated. Claim: legacy's own code run over 120 random weeks (packages/core/vectors/legacy-schedule-figures).
import { money, ratio, type Currency, type Money } from '@radish/primitives';
import { round } from '../money/index.js';
import { f64, f64Add, f64Compare, f64Div, f64Fmod, f64Mul, f64Sub, jsRound, jsToFixedScaled, phpRound, type F64 } from '../money/binary64.js';

export type ScheduleArithmetic = 'legacy_binary64' | 'exact';
/** Legacy's arithmetic until parity is validated (Andrew, 2026-10-12); `exact` is the B339 correction. */
export const SCHEDULE_ARITHMETIC: ScheduleArithmetic = 'legacy_binary64';

export interface FigureShift {
  readonly shiftId: string;
  /** Null: an open shift. */
  readonly employeeId: string | null;
  readonly jobRoleId: string | null;
  readonly businessDate: string;
  /** "HH:MM". */
  readonly startTime: string;
  readonly endTime: string;
  /** The span less the unpaid planned breaks. */
  readonly netMinutes: number;
  /** Minor units per hour (core `shiftRate`); 0 when none is on file. */
  readonly rate: bigint;
}

/** Legacy `resolveWageInCents(role.wage, employee.wage)`: the role's rate when above zero, else the employee's, else 0. */
export const shiftRate = (roleWage: Money | null, employeeWage: Money | null): bigint =>
  roleWage !== null && roleWage.minor > 0n ? roleWage.minor : employeeWage !== null && employeeWage.minor > 0n ? employeeWage.minor : 0n;

const ZERO = f64(0n);
const SIXTY = f64(60n);
const HUNDRED = f64(100n);
/** The API's `net_hours`: PHP 8.2 `round(minutes / 60, 2)`. */
const netHours = (minutes: number): F64 => phpRound(f64Div(f64(BigInt(minutes)), SIXTY), 2);

/** A count of hundredths (or tenths) as JavaScript prints `Number(x.toFixed(d))`: "7.3", "8", "0.08". */
const decimalText = (units: bigint, digits: number): string => {
  const s = units.toString().padStart(digits + 1, '0');
  const whole = s.slice(0, -digits);
  const fraction = s.slice(-digits).replace(/0+$/, '');
  return fraction === '' ? whole : `${whole}.${fraction}`;
};

/** Legacy `formatHours(sum of paidHoursFor)`: "7.3 hrs". */
export function scheduleHoursLabel(shifts: readonly Pick<FigureShift, 'netMinutes'>[], arithmetic: ScheduleArithmetic = SCHEDULE_ARITHMETIC): string {
  if (arithmetic === 'exact') {
    const minutes = shifts.reduce((n, s) => n + s.netMinutes, 0);
    return `${decimalText(round(ratio(BigInt(minutes), 6n), 'HALF_UP'), 1)} hrs`;
  }
  const hours = shifts.reduce((total, s) => f64Add(total, netHours(s.netMinutes)), ZERO);
  return `${decimalText(jsToFixedScaled(hours, 1), 1)} hrs`;
}

/** Legacy useSchedulePdf's hours: the shifts' hours summed, `toFixed(1)` ("8.0"). */
export function scheduleHoursFixed(shifts: readonly Pick<FigureShift, 'netMinutes'>[]): string {
  const units = jsToFixedScaled(shifts.reduce((total, s) => f64Add(total, netHours(s.netMinutes)), ZERO), 1).toString().padStart(2, '0');
  return `${units.slice(0, -1)}.${units.slice(-1)}`;
}

/** Legacy `totalCostFor`: the planned cost in minor units, or null (a dash) when no shift has a rate. */
export function schedulePlannedCost(shifts: readonly Pick<FigureShift, 'netMinutes' | 'rate'>[], arithmetic: ScheduleArithmetic = SCHEDULE_ARITHMETIC): bigint | null {
  if (shifts.length === 0 || !shifts.some((s) => s.rate > 0n)) return null;
  if (arithmetic === 'exact') return round(ratio(shifts.reduce((n, s) => n + s.rate * BigInt(s.netMinutes), 0n), 60n), 'HALF_UP');
  const cents = shifts.reduce((total, s) => f64Add(total, f64Mul(netHours(s.netMinutes), f64(s.rate))), ZERO);
  return jsToFixedScaled(f64Div(cents, HUNDRED), 2);
}

/** The same as Money, for the server's week figures. */
export function schedulePlannedMoney(shifts: readonly Pick<FigureShift, 'netMinutes' | 'rate'>[], currency: Currency, arithmetic: ScheduleArithmetic = SCHEDULE_ARITHMETIC): Money | null {
  const minor = schedulePlannedCost(shifts, arithmetic);
  return minor === null ? null : money(minor, currency);
}

// ---- the Day view -------------------------------------------------------------------------------------------------

const DAY = f64(24n);
const START_HOUR = f64(4n);
const BUCKET = 2;

/** Legacy `toBusinessDayOffset(timeToHours(t))`: hours since 4am, as a double. */
const offsetOf = (t: string): F64 => {
  const hours = f64Add(f64(BigInt(Number(t.slice(0, 2)))), f64Div(f64(BigInt(Number(t.slice(3, 5)))), SIXTY));
  return f64Fmod(f64Add(f64Sub(hours, START_HOUR), DAY), DAY);
};
/** Legacy `shiftOffsetSpan`: an end not after the start runs into the next day. */
const offsetSpan = (s: Pick<FigureShift, 'startTime' | 'endTime'>): { start: F64; end: F64 } => {
  const start = offsetOf(s.startTime);
  const end = offsetOf(s.endTime);
  return { start, end: f64Compare(end, start) <= 0 ? f64Add(end, DAY) : end };
};
const max = (a: F64, b: F64): F64 => (f64Compare(a, b) >= 0 ? a : b);
const min = (a: F64, b: F64): F64 => (f64Compare(a, b) <= 0 ? a : b);
const toNumber = (x: F64): number => Number(x.num) / Number(x.den);
const HALF = f64Div(f64(1n), f64(2n));

export interface DayBar {
  readonly shiftId: string;
  readonly employeeId: string | null;
  /** The tail of a shift the day before owns (read-only here; a click opens its day). */
  readonly continuation: boolean;
  /** Hours from 4am to the bar's left edge, and its width in hours (at least half an hour). */
  readonly left: number;
  readonly width: number;
}

export interface DayBucket {
  /** "4am", "6am" … "2am". */
  readonly label: string;
  /** Shifts covering any part of the two hours. */
  readonly count: number;
  /** "1.5 hrs" (to two decimals; gross of unpaid breaks, so the columns need not sum to the day). */
  readonly hours: string;
  /** Minor units, or null (a dash) when nothing covering it has a rate. */
  readonly cost: bigint | null;
}

export interface ScheduleDay {
  readonly bars: readonly DayBar[];
  readonly buckets: readonly DayBucket[];
  /** The shifts the day owns (continuations left out): each row's and the day's hours and cost come from these. */
  readonly owned: readonly FigureShift[];
  /** Whether the Open Shifts row shows (an open shift is drawn on the day). */
  readonly hasOpen: boolean;
}

const hourLabel = (offset: number): string => {
  const h = (offset + 4) % 24;
  return `${String(h % 12 === 0 ? 12 : h % 12)}${h >= 12 ? 'pm' : 'am'}`;
};

/**
 * Legacy `ScheduleDayView` for one business day: the shifts of the week's schedule drawn on it (its own, and the
 * tails of the day before's running past 4am), the twelve two-hour columns and the shifts the day owns.
 */
export function scheduleDay(date: string, previousDate: string, shifts: readonly FigureShift[]): ScheduleDay {
  const spans = shifts.flatMap((s) => {
    const span = offsetSpan(s);
    if (s.businessDate === date) return [{ s, start: span.start, end: span.end, continuation: false }];
    if (s.businessDate === previousDate && f64Compare(span.end, DAY) > 0) return [{ s, start: f64Sub(span.start, DAY), end: f64Sub(span.end, DAY), continuation: true }];
    return [];
  });
  const bars = spans.map(({ s, start, end, continuation }): DayBar => {
    const left = max(ZERO, start);
    const width = max(HALF, f64Sub(min(DAY, end), left));
    return { shiftId: s.shiftId, employeeId: s.employeeId, continuation, left: toNumber(left), width: toNumber(width) };
  });
  const buckets = Array.from({ length: 12 }, (_, i): DayBucket => {
    const offset = f64(BigInt(i * BUCKET));
    const offsetEnd = f64(BigInt(i * BUCKET + BUCKET));
    const minutesIn = spans.map((x) => {
      const from = max(x.start, offset);
      const to = min(x.end, offsetEnd);
      return { x, minutes: max(ZERO, f64Mul(f64Sub(to, from), SIXTY)) };
    });
    const count = spans.filter((x) => f64Compare(x.start, offsetEnd) < 0 && f64Compare(x.end, offset) > 0).length;
    const minutes = minutesIn.reduce((total, m) => f64Add(total, m.minutes), ZERO);
    const covering = minutesIn.filter((m) => m.minutes.num > 0n);
    const cost = covering.some((m) => m.x.s.rate > 0n)
      ? jsRound(covering.reduce((total, m) => f64Add(total, f64Mul(f64Div(m.minutes, SIXTY), f64(m.x.s.rate))), ZERO))
      : null;
    return { label: hourLabel(i * BUCKET), count, hours: `${decimalText(jsToFixedScaled(f64Div(minutes, SIXTY), 2), 2)} hrs`, cost };
  });
  return { bars, buckets, owned: spans.filter((x) => !x.continuation).map((x) => x.s), hasOpen: spans.some((x) => x.s.employeeId === null) };
}

